import React, { useEffect, useState } from "react";
import { Chart } from "primereact/chart";
import { handleGetRequest } from "../../services/GetTemplate";
import styles from "./FinancialAnalytics.module.scss";
import { FiTrendingUp, FiDollarSign, FiCreditCard, FiPieChart, FiCalendar } from "react-icons/fi";
import InvalidInvoicesList from "./InvalidInvoices";

const FinancialAnalytics = () => {
    const [summary, setSummary] = useState(null);
    const [projectionData, setProjectionData] = useState(null);
    const [userBreakdown, setUserBreakdown] = useState([]);
    const [invoiceStatus, setInvoiceStatus] = useState(null);
    const [feeComposition, setFeeComposition] = useState(null);
    const [discounts, setDiscounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Date range states
    const [dateRange, setDateRange] = useState("month");
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
    const [projectionMonths, setProjectionMonths] = useState(6);
    const [projectionType, setProjectionType] = useState("paid"); // "paid" or "invoice"

    const getCurrentDate = () => {
        const today = new Date();
        return today.toISOString().split("T")[0];
    };

    const getDateRange = () => {
        const today = new Date();
        let startDate, endDate;

        switch (dateRange) {
            case "week":
                startDate = new Date(today);
                startDate.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
                endDate = new Date();
                break;
            case "month":
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = new Date();
                break;
            case "year":
                startDate = new Date(today.getFullYear(), 0, 1);
                endDate = new Date();
                break;
            case "custom":
                startDate = customStartDate ? new Date(customStartDate) : new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = customEndDate ? new Date(customEndDate) : new Date();
                break;
            default:
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = new Date();
        }

        return {
            startDate: startDate.toISOString().split("T")[0],
            endDate: endDate.toISOString().split("T")[0],
        };
    };

    useEffect(() => {
        fetchFinancialAnalytics();
    }, [dateRange, customStartDate, customEndDate, projectionMonths, projectionType]);

    const fetchFinancialAnalytics = async () => {
        try {
            setLoading(true);
            setError(null);

            const { startDate, endDate } = getDateRange();

            const [sum, projPaid, projInvoice, users, invoice, fees, disc] = await Promise.all([
                handleGetRequest(`/analytics/financial/summary?startDate=${startDate}&endDate=${endDate}`).catch((e) => {
                    console.error("Summary error:", e);
                    return null;
                }),
                handleGetRequest(`/analytics/financial/projection?months=${projectionMonths}&type=paid&startDate=${startDate}&endDate=${endDate}`).catch((e) => {
                    console.error("Paid projection error:", e);
                    return null;
                }),
                handleGetRequest(`/analytics/financial/projection?months=${projectionMonths}&type=invoice&startDate=${startDate}&endDate=${endDate}`).catch((e) => {
                    console.error("Invoice projection error:", e);
                    return null;
                }),
                handleGetRequest(`/analytics/financial/user-breakdown?limit=10&startDate=${startDate}&endDate=${endDate}`).catch((e) => {
                    console.error("User breakdown error:", e);
                    return null;
                }),
                handleGetRequest(`/analytics/financial/invoice-status?startDate=${startDate}&endDate=${endDate}`).catch((e) => {
                    console.error("Invoice status error:", e);
                    return null;
                }),
                handleGetRequest(`/analytics/financial/fee-composition?startDate=${startDate}&endDate=${endDate}`).catch((e) => {
                    console.error("Fee composition error:", e);
                    return null;
                }),
                handleGetRequest(`/analytics/financial/discount-credits?limit=10&startDate=${startDate}&endDate=${endDate}`).catch((e) => {
                    console.error("Discounts error:", e);
                    return null;
                }),
            ]);

            // Process summary
            if (sum?.data?.summary?.[0]) {
                setSummary(sum.data.summary[0]);
            }

            // Process projections - combine both types
            const combinedProjection = {
                paid: null,
                invoice: null,
            };

            if (projPaid?.data?.projections) {
                combinedProjection.paid = {
                    labels: projPaid.data.projections.map((item) => `${item.month}/${item.year}`),
                    datasets: [
                        {
                            label: "Projected Paid Amount",
                            data: projPaid.data.projections.map((item) => item.projectedRevenue),
                            borderColor: "#10b981",
                            backgroundColor: "rgba(16, 185, 129, 0.1)",
                            fill: true,
                            tension: 0.4,
                            borderWidth: 2,
                            pointBackgroundColor: "#10b981",
                            pointBorderColor: "#fff",
                            pointBorderWidth: 2,
                            pointRadius: 5,
                        },
                    ],
                    metadata: projPaid.data,
                };
            }

            if (projInvoice?.data?.projections) {
                combinedProjection.invoice = {
                    labels: projInvoice.data.projections.map((item) => `${item.month}/${item.year}`),
                    datasets: [
                        {
                            label: "Projected Invoice Amount",
                            data: projInvoice.data.projections.map((item) => item.projectedRevenue),
                            borderColor: "#f59e0b",
                            backgroundColor: "rgba(245, 158, 11, 0.1)",
                            fill: true,
                            tension: 0.4,
                            borderWidth: 2,
                            pointBackgroundColor: "#f59e0b",
                            pointBorderColor: "#fff",
                            pointBorderWidth: 2,
                            pointRadius: 5,
                        },
                    ],
                    metadata: projInvoice.data,
                };
            }

            setProjectionData(combinedProjection);

            setUserBreakdown(users?.data || []);

            if (invoice?.data?.[0]) {
                const data = invoice.data[0];
                setInvoiceStatus({
                    paymentLabels: data.paymentStatus.map((item) => item.status),
                    paymentData: data.paymentStatus.map((item) => item.count),
                    reviewLabels: data.reviewStatus.map((item) => item.status),
                    reviewData: data.reviewStatus.map((item) => item.count),
                });
            }

            if (fees?.data?.[0]?.breakdown) {
                const breakdown = fees.data[0].breakdown.filter((item) => item.amount > 0);
                setFeeComposition({
                    labels: breakdown.map((item) => item.category),
                    datasets: [
                        {
                            data: breakdown.map((item) => item.amount),
                            backgroundColor: ["#667eea", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4"],
                            borderColor: "#fff",
                            borderWidth: 2,
                        },
                    ],
                });
            }

            setDiscounts(disc?.data || []);
        } catch (error) {
            console.error("Error fetching financial analytics:", error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDateRangeChange = (range) => {
        setDateRange(range);
        if (range !== "custom") {
            setShowCustomDatePicker(false);
        }
    };

    const handleCustomDateSubmit = () => {
        if (customStartDate && customEndDate) {
            if (new Date(customStartDate) > new Date(customEndDate)) {
                setError("Start date must be before end date");
                return;
            }
            setShowCustomDatePicker(false);
            setDateRange("custom");
        } else {
            setError("Please select both start and end dates");
        }
    };

    const getStatusColor = (paymentRate) => {
        if (paymentRate >= 80) return styles.statusGood;
        if (paymentRate >= 50) return styles.statusPartial;
        return styles.statusOverdue;
    };

    const getStatusLabel = (paymentRate) => {
        if (paymentRate >= 80) return "✓ Good";
        if (paymentRate >= 50) return "⏳ Partial";
        return "⚠ Overdue";
    };

    const chartOptions = {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        plugins: {
            legend: {
                labels: {
                    color: "#495057",
                    font: { size: 12 },
                },
            },
        },
        scales: {
            x: {
                ticks: { color: "#495057" },
                grid: { color: "#ebedef" },
            },
            y: {
                ticks: { color: "#495057" },
                grid: { color: "#ebedef" },
            },
        },
    };

    const pieOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                labels: {
                    color: "#495057",
                    font: { size: 12 },
                    padding: 15,
                },
                position: "bottom",
            },
        },
    };

    if (loading) {
        return <div className={styles.loading}>Loading Financial Analytics...</div>;
    }

    const { startDate, endDate } = getDateRange();
    const activeProjection = projectionType === "paid" ? projectionData?.paid : projectionData?.invoice;
    const projectionMetadata = activeProjection?.metadata;

    return (
        <div className={styles.financialAnalytics}>
            <div className={styles.headerSection}>
                <h2 className={styles.sectionTitle}>💰 Financial Analytics</h2>

                {/* Date Range Filter */}
                <div className={styles.filterSection}>
                    <div className={styles.dateRangeButtons}>
                        <button className={`${styles.rangeButton} ${dateRange === "week" ? styles.active : ""}`} onClick={() => handleDateRangeChange("week")}>
                            📅 This Week
                        </button>
                        <button className={`${styles.rangeButton} ${dateRange === "month" ? styles.active : ""}`} onClick={() => handleDateRangeChange("month")}>
                            📆 This Month
                        </button>
                        <button className={`${styles.rangeButton} ${dateRange === "year" ? styles.active : ""}`} onClick={() => handleDateRangeChange("year")}>
                            📊 This Year
                        </button>
                        <button
                            className={`${styles.rangeButton} ${dateRange === "custom" ? styles.active : ""}`}
                            onClick={() => {
                                setDateRange("custom");
                                setShowCustomDatePicker(!showCustomDatePicker);
                            }}
                        >
                            <FiCalendar /> Custom
                        </button>
                    </div>

                    {/* Custom Date Range Picker */}
                    {showCustomDatePicker && (
                        <div className={styles.customDatePicker}>
                            <div className={styles.dateInputGroup}>
                                <div className={styles.dateInput}>
                                    <label>Start Date</label>
                                    <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} max={getCurrentDate()} />
                                </div>
                                <div className={styles.dateInput}>
                                    <label>End Date</label>
                                    <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} max={getCurrentDate()} />
                                </div>
                            </div>
                            <div className={styles.dateActions}>
                                <button className={styles.applyButton} onClick={handleCustomDateSubmit}>
                                    Apply
                                </button>
                                <button className={styles.cancelButton} onClick={() => setShowCustomDatePicker(false)}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Date Range Display */}
                    <div className={styles.dateDisplay}>
                        📍 {startDate} to {endDate}
                    </div>
                </div>
            </div>

            {error && <div className={styles.errorMessage}>⚠️ {error}</div>}

            {/* Summary Cards */}
            {summary && (
                <div className={styles.summaryGrid}>
                    <div className={`${styles.summaryCard} ${styles.primary}`}>
                        <div className={styles.cardIcon}>
                            <FiDollarSign />
                        </div>
                        <div className={styles.cardContent}>
                            <p className={styles.cardLabel}>Total Revenue</p>
                            <p className={styles.cardValue}>${(summary.totalInvoiced || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}</p>
                            <p className={styles.cardDesc}>{summary.invoiceCount || 0} invoices</p>
                        </div>
                    </div>

                    <div className={`${styles.summaryCard} ${styles.success}`}>
                        <div className={styles.cardIcon}>
                            <FiCreditCard />
                        </div>
                        <div className={styles.cardContent}>
                            <p className={styles.cardLabel}>Total Paid</p>
                            <p className={styles.cardValue}>${(summary.totalPaid || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}</p>
                            <p className={styles.cardDesc}>{summary.paidInvoices || 0} invoices</p>
                        </div>
                    </div>

                    <div className={`${styles.summaryCard} ${styles.warning}`}>
                        <div className={styles.cardIcon}>
                            <FiTrendingUp />
                        </div>
                        <div className={styles.cardContent}>
                            <p className={styles.cardLabel}>Pending Amount</p>
                            <p className={styles.cardValue}>${(summary.totalPending || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}</p>
                            <p className={styles.cardDesc}>{summary.pendingInvoices || 0} invoices</p>
                        </div>
                    </div>

                    <div className={`${styles.summaryCard} ${styles.info}`}>
                        <div className={styles.cardIcon}>
                            <FiPieChart />
                        </div>
                        <div className={styles.cardContent}>
                            <p className={styles.cardLabel}>Collection Rate</p>
                            <p className={styles.cardValue}>{summary.invoiceCount > 0 ? ((summary.paidInvoices / summary.invoiceCount) * 100).toFixed(1) : 0}%</p>
                            <p className={styles.cardDesc}>Payment efficiency</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Charts */}
            <div className={styles.chartsGrid}>
                {/* Revenue Projection */}
                {activeProjection && (
                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <h3 className={styles.chartTitle}>📊 Revenue Projection</h3>
                            <div className={styles.projectionControls}>
                                <div className={styles.projectionToggle}>
                                    <button className={`${styles.toggleBtn} ${projectionType === "paid" ? styles.active : ""}`} onClick={() => setProjectionType("paid")}>
                                        💳 Paid Amount
                                    </button>
                                    <button className={`${styles.toggleBtn} ${projectionType === "invoice" ? styles.active : ""}`} onClick={() => setProjectionType("invoice")}>
                                        📄 Invoice Amount
                                    </button>
                                </div>
                                <div className={styles.projectionToggle}>
                                    <button className={`${styles.toggleBtn} ${projectionMonths === 6 ? styles.active : ""}`} onClick={() => setProjectionMonths(6)}>
                                        6M
                                    </button>
                                    <button className={`${styles.toggleBtn} ${projectionMonths === 12 ? styles.active : ""}`} onClick={() => setProjectionMonths(12)}>
                                        1Y
                                    </button>
                                </div>
                            </div>
                        </div>
                        <Chart type="line" data={activeProjection} options={chartOptions} />

                        {/* Projection Stats */}
                        {projectionMetadata && (
                            <div className={styles.projectionStats}>
                                <div className={styles.statItem}>
                                    <span className={styles.statLabel}>Avg Monthly:</span>
                                    <span className={styles.statValue}>${projectionMetadata.avgMonthlyRevenue?.toLocaleString()}</span>
                                </div>
                                <div className={styles.statItem}>
                                    <span className={styles.statLabel}>Growth Rate:</span>
                                    <span className={`${styles.statValue} ${parseFloat(projectionMetadata.growthRate) >= 0 ? styles.positive : styles.negative}`}>{projectionMetadata.growthRate}</span>
                                </div>
                                <div className={styles.statItem}>
                                    <span className={styles.statLabel}>Method:</span>
                                    <span className={styles.statValue}>{projectionMetadata.projectionMethod}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Fee Composition */}
                {feeComposition && (
                    <div className={styles.chartCard}>
                        <h3 className={styles.chartTitle}>💳 Fee Composition</h3>
                        <Chart type="doughnut" data={feeComposition} options={pieOptions} />
                    </div>
                )}
            </div>

            {/* Invoice Status */}
            {invoiceStatus && (
                <div className={styles.statusGrid}>
                    <div className={styles.statusCard}>
                        <h3 className={styles.statusTitle}>📈 Payment Status</h3>
                        <div className={styles.statusList}>
                            {invoiceStatus.paymentLabels.map((label, idx) => (
                                <div key={idx} className={styles.statusItem}>
                                    <div className={styles.statusLabel}>
                                        <span className={styles.statusBadge}></span>
                                        {label}
                                    </div>
                                    <span className={styles.statusCount}>{invoiceStatus.paymentData[idx]}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.statusCard}>
                        <h3 className={styles.statusTitle}>✅ Review Status</h3>
                        <div className={styles.statusList}>
                            {invoiceStatus.reviewLabels.map((label, idx) => (
                                <div key={idx} className={styles.statusItem}>
                                    <div className={styles.statusLabel}>
                                        <span className={styles.statusBadge}></span>
                                        {label}
                                    </div>
                                    <span className={styles.statusCount}>{invoiceStatus.reviewData[idx]}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* User Financial Breakdown */}
            <div className={styles.tableCard}>
                <h3 className={styles.tableTitle}>👥 User Financial Breakdown</h3>
                {userBreakdown.length > 0 ? (
                    <>
                        {userBreakdown.some((u) => u.hasDataIssue) && <div className={styles.dataWarning}>⚠️ Some users have data inconsistencies (Paid is greater than Revenue). Please verify invoice data.</div>}
                        <div className={styles.tableWrapper}>
                            <table className={styles.analyticsTable}>
                                <thead>
                                    <tr>
                                        <th>User Name</th>
                                        <th>Total Revenue</th>
                                        <th>Paid</th>
                                        <th>Pending</th>
                                        <th>Payment Rate</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {userBreakdown.map((user, idx) => (
                                        <tr key={idx} className={user.hasDataIssue ? styles.rowWarning : ""}>
                                            <td className={styles.userName}>{user.clientName}</td>
                                            <td className={styles.currency}>${(user.totalRevenue || 0).toLocaleString()}</td>
                                            <td className={styles.success}>${(user.totalPaid || 0).toLocaleString()}</td>
                                            <td className={styles.warning}>${(user.totalPending || 0).toLocaleString()}</td>
                                            <td className={styles.percent}>{(user.paymentRate || 0).toFixed(1)}%</td>
                                            <td>
                                                <span className={`${styles.statusBadge2} ${getStatusColor(user.paymentRate || 0)}`}>{getStatusLabel(user.paymentRate || 0)}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : (
                    <div className={styles.emptyState}>
                        <p>No user financial data available</p>
                    </div>
                )}
            </div>

            <InvalidInvoicesList startDate={startDate} endDate={endDate} />

            {/* Top Discount Recipients */}
            <div className={styles.tableCard}>
                <h3 className={styles.tableTitle}>🎁 Top Discount & Credits Recipients</h3>
                {discounts.length > 0 ? (
                    <div className={styles.tableWrapper}>
                        <table className={styles.analyticsTable}>
                            <thead>
                                <tr>
                                    <th>Rank</th>
                                    <th>Client Name</th>
                                    <th>Loyalty Discount</th>
                                    <th>Credits Used</th>
                                    <th>Total Benefit</th>
                                    <th>Avg Discount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {discounts.map((discount, idx) => (
                                    <tr key={idx}>
                                        <td>#{idx + 1}</td>
                                        <td className={styles.userName}>{discount.clientName}</td>
                                        <td className={styles.currency}>${(discount.totalLoyaltyDiscount || 0).toLocaleString()}</td>
                                        <td className={styles.currency}>${(discount.totalCreditsUsed || 0).toLocaleString()}</td>
                                        <td className={styles.highlight}>${((discount.totalLoyaltyDiscount || 0) + (discount.totalCreditsUsed || 0)).toLocaleString()}</td>
                                        <td>${(discount.avgDiscount || 0).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={styles.emptyState}>
                        <p>No discount data available</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FinancialAnalytics;
